import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, Row, Col, Tag, message, Spin, Typography, Button, Popconfirm } from 'antd';
import { CalendarOutlined, ReloadOutlined, EnvironmentOutlined, ClockCircleOutlined, DollarOutlined, CloseCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { apiClient } from '../../../config/apiClient';
import MainLayout from '../../layout/layout';

const { Text } = Typography;
const DIAS_SEMANA = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];

const JugadorMisTurnos = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [turnos, setTurnos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelandoId, setCancelandoId] = useState(null);

  useEffect(() => {
    const mp = searchParams.get('mp');
    if (mp === 'success') {
      message.success('Seña abonada. Tu reserva quedó confirmada.');
      setSearchParams({}, { replace: true });
      loadTurnos(); // Refrescar para mostrar estado actualizado
    } else if (mp === 'pending') {
      message.info('El pago está pendiente. La reserva se confirmará cuando se acredite.');
      setSearchParams({}, { replace: true });
      loadTurnos();
    }
  }, [searchParams, setSearchParams]);

  const loadTurnos = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/club/mi/turnos');
      if (response.ok) {
        const data = await response.json();
        setTurnos(Array.isArray(data) ? data : []);
      } else {
        const err = await response.json();
        message.error(err.message || 'Error al cargar tus turnos');
        setTurnos([]);
      }
    } catch (error) {
      console.error('Error al cargar mis turnos:', error);
      message.error('Error al cargar tus turnos');
      setTurnos([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTurnos();
  }, []);

  const renderFechaTipo = (record) => {
    if (record.tcl_tipo === 'FIJO' && record.tcl_dia_semana != null) {
      const dia = DIAS_SEMANA[record.tcl_dia_semana] || `Día ${record.tcl_dia_semana}`;
      return (
        <span>
          <Tag color="blue">Fijo</Tag> {dia}
        </span>
      );
    }
    return record.tcl_fecha ? dayjs(record.tcl_fecha).format('DD/MM/YYYY') : '-';
  };

  const renderEstado = (estado) => {
    const color = estado === 'RESERVADO' ? 'green' : estado === 'BLOQUEADO' ? 'orange' : 'default';
    return <Tag color={color}>{estado || '-'}</Tag>;
  };

  const renderEstadoPago = (estadoPago) => {
    if (!estadoPago) return null;
    const ep = String(estadoPago).toLowerCase();
    if (ep === 'pagado') return <Tag color="green" style={{ marginLeft: 4 }}>Pagado</Tag>;
    if (ep === 'pendiente') return <Tag color="orange" style={{ marginLeft: 4 }}>Pago pendiente</Tag>;
    if (ep === 'cancelado') return <Tag style={{ marginLeft: 4 }}>Sin seña</Tag>;
    return null;
  };

  // Turno ya pasó (solo para UNICO con fecha y hora): no se puede cancelar
  const turnoYaPasado = (turno) => {
    if (turno.tcl_tipo !== 'UNICO' || !turno.tcl_fecha || !turno.tcl_hora_hasta) return false;
    const fechaStr = dayjs(turno.tcl_fecha).format('YYYY-MM-DD');
    const horaHasta = String(turno.tcl_hora_hasta).substring(0, 5);
    const finTurno = dayjs(`${fechaStr} ${horaHasta}`, 'YYYY-MM-DD HH:mm');
    return finTurno.isBefore(dayjs());
  };

  const handleCancelarTurno = async (turno) => {
    if (!turno.rcl_id) return;
    setCancelandoId(turno.rcl_id);
    try {
      const response = await apiClient.delete(`/club/mi/reservas/${turno.rcl_id}`);
      if (response.ok) {
        message.success('Turno cancelado. El lugar quedó liberado.');
        loadTurnos();
      } else {
        const err = await response.json();
        message.error(err.message || 'Error al cancelar el turno');
      }
    } catch (error) {
      console.error('Error al cancelar:', error);
      message.error('Error al cancelar el turno');
    } finally {
      setCancelandoId(null);
    }
  };

  return (
    <MainLayout>
      <Card
        title={
          <span>
            <CalendarOutlined style={{ marginRight: 8 }} />
            Mis Turnos
          </span>
        }
        extra={
          <ReloadOutlined
            spin={loading}
            onClick={loadTurnos}
            style={{ cursor: 'pointer', fontSize: 18 }}
            title="Actualizar"
          />
        }
      >
        <Spin spinning={loading} tip="Cargando tus turnos...">
          {turnos.length === 0 && !loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'rgba(255,255,255,0.45)' }}>
              No tenés turnos reservados
            </div>
          ) : (
            <>
              <div style={{ marginBottom: 16 }}>
                <Text type="secondary">Total: {turnos.length} turno{turnos.length !== 1 ? 's' : ''}</Text>
              </div>
              <Row gutter={[16, 16]}>
                {turnos.map((turno) => {
                  const horario = turno.tcl_hora_desde && turno.tcl_hora_hasta
                    ? `${turno.tcl_hora_desde.substring(0, 5)} - ${turno.tcl_hora_hasta.substring(0, 5)}`
                    : '-';
                  const precio = turno.tcl_precio != null ? `$ ${Number(turno.tcl_precio).toFixed(2)}` : '-';
                  return (
                    <Col xs={24} sm={24} md={12} lg={8} xl={6} key={turno.rcl_id}>
                      <Card
                        size="small"
                        style={{ height: '100%' }}
                        styles={{
                          body: { padding: 16 },
                        }}
                      >
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
                            <span>{renderFechaTipo(turno)}</span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                              {renderEstado(turno.tcl_estado)}
                              {renderEstadoPago(turno.rcl_estado_pago)}
                            </div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <EnvironmentOutlined style={{ color: 'rgba(255,255,255,0.45)' }} />
                            <Text>{turno.cancha_nombre || '-'}</Text>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <ClockCircleOutlined style={{ color: 'rgba(255,255,255,0.45)' }} />
                            <Text>{horario}</Text>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <DollarOutlined style={{ color: 'rgba(255,255,255,0.45)' }} />
                            <Text strong>{precio}</Text>
                          </div>
                          {turno.tcl_estado === 'RESERVADO' && !turnoYaPasado(turno) && (
                            <Popconfirm
                              title="Cancelar turno"
                              description="¿Querés cancelar este turno? El lugar quedará liberado para otros."
                              onConfirm={() => handleCancelarTurno(turno)}
                              okText="Sí, cancelar"
                              cancelText="No"
                              okButtonProps={{ danger: true }}
                            >
                              <Button
                                type="primary"
                                danger
                                size="small"
                                icon={<CloseCircleOutlined />}
                                loading={cancelandoId === turno.rcl_id}
                                block
                                style={{ marginTop: 8 }}
                              >
                                Cancelar turno
                              </Button>
                            </Popconfirm>
                          )}
                        </div>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            </>
          )}
        </Spin>
      </Card>
    </MainLayout>
  );
};

export default JugadorMisTurnos;
